import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Store';
import { colors, css, fonts, glyphs, useMediaQuery } from '../../../Styles';
import { NewInput, NewSelect, Overlay, SecondaryPopover, Setting } from '../..';
import { NewFieldStyles, NewSelectHandles, NewInputHandles, ButtonTypes, ListStyle_Types, LocaleFarmDate } from '../../../Definitions';
import { Graph_QL, useUser, useMilestones } from "../../../Services"
import timeZoneOptions from '../../../Assets/timeZones';

// TODO
import NewTimePicker, { convertMinutesFromMidnightToHour, convertMinutesFromMidnightToMinute } from '../../BaseComponents/newTimePicker';
import { DateTime } from 'luxon';
import NewButton, { NewButtonList } from '../../BaseComponents/newButton';
import { createPortal } from 'react-dom';


export default function SystemSettings({onDirtyChange, zIndex}:{onDirtyChange: (dirty: boolean) => void, zIndex?: number}) {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);
  
  // #region | GraphQL 
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { userToken, farmSettings, getFarmSettings, updateFarmSettings } = useUser();
  useEffect(() => {
    if (userToken) {
      if (farm) {
        getFarmSettings(farm);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm]);

  const {data: milestonesState, call: getMilestones, maintainEntries: maintainMilestones} = useMilestones(connection);
  const milestones = useMemo(() => {
    if (milestonesState[domain]) {
      return milestonesState[domain].data
    }
    return []
  }, [domain, milestonesState])

  useEffect(() => {
    getMilestones({fetchAmount: 500}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion

  // #region | Time Zone
  const timeZoneRef = useRef<NewSelectHandles>(null);
  const [timeZoneChanged, setTimeZoneChanged] = useState(false);
 
  useEffect(() => {
    if (farmSettings?.time_zone_label) {
      const timeZoneLabel = farmSettings.time_zone_label.replace(/_/g," ")
      const findSelectId = timeZoneOptions.find(({label}) => label === timeZoneLabel)?.id;
      timeZoneRef.current?.setValue({label: timeZoneLabel , id: findSelectId !== undefined ? findSelectId : -1})
    }
  },[farmSettings?.time_zone_label])
  // #endregion

  // #region | Feed Day Start
  const timePickerValueRef = useRef<{hour: string | undefined, minute: string | undefined}>({hour: undefined, minute: undefined});
  const [defaultFeedDayStart, setDefaultFeedDayStart] = useState({defaultHour: convertMinutesFromMidnightToHour(farmSettings?.feed_day_start ?? 0), defaultMinute: convertMinutesFromMidnightToMinute(farmSettings?.feed_day_start ?? 0)})
  const [feedDayStartChanged, setFeedDayStartChanged] = useState(false);

  useEffect(() => {
    if (farmSettings?.feed_day_start) {
      timePickerValueRef.current = {hour: undefined, minute: undefined}
    }
  }, [farmSettings?.feed_day_start])
  
  useEffect(() => {
    setDefaultFeedDayStart({defaultHour: convertMinutesFromMidnightToHour(farmSettings?.feed_day_start ?? 0), defaultMinute: convertMinutesFromMidnightToMinute(farmSettings?.feed_day_start ?? 0)})
  }, [farmSettings?.feed_day_start])

  // #endregion

  // #region | Milestones
  const milestoneNameRef = useRef<NewInputHandles>(null);

  const [localMilestones, setLocalMilestones] = useState<typeof milestones>([]);

  useEffect(() => {
    setLocalMilestones(milestones);
  }, [milestones]);

  const handleAddMilestone = () => {
    const value = milestoneNameRef.current?.value;
    if (value) {
      const newMilestone = {
        milestone_id: Date.now(), // midlertidigt id
        name: String(value),
        deleted: false
      };
      setLocalMilestones(prev => [...prev, newMilestone]);
    };
    if (milestoneNameRef.current?.value) {
      milestoneNameRef.current.setValue("")
    }
  };

  const handleDeleteMilestone = (milestone_id: number) => {
    setLocalMilestones(prev =>
      prev.map(m => m.milestone_id === milestone_id ? {...m, deleted: true} : m)
    );
  };
  // #endregion

  // #region | Units
  const unitsOptions = [    
    { label: t("globalSettingsPages.globalSettings.metric", {defaultValue: "metric"}), id: 0 },
    { label: t("globalSettingsPages.globalSettings.imperial", {defaultValue: "imperial"}), id: 1 },
  ]

  const [unit, setUnit] = useState<number>(0)
  // #endregion

  // Dirty Data
  const dirtyData = useMemo(() => {
    const dbNames = milestones.filter(m => !m.deleted).map(m => m.name).sort();
    const localNames = localMilestones.filter(m => !m.deleted).map(m => m.name).sort();
  
    const milestonesChanged = JSON.stringify(dbNames) !== JSON.stringify(localNames);
  
    return timeZoneChanged || milestonesChanged || feedDayStartChanged;
  }, [localMilestones, milestones, timeZoneChanged, feedDayStartChanged]);

  useEffect(() => {
    onDirtyChange?.(dirtyData);
  }, [dirtyData, onDirtyChange]);

  return ( <>
    <div style={{ position: "relative", zIndex: zIndex ? zIndex : 0, display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%" }}>

      {/* Time Zone */}
      <Setting startEnhancer={glyphs.Time({size: "25px"})} header={t("globalSettingsPages.globalSettings.timeZone", {defaultValue: "time zone"})} content={
        <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
          <NewSelect
            ref={timeZoneRef}
            options={timeZoneOptions}
            onChange={(e) => setTimeZoneChanged(true)}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary
            }}
            isSearchable
          />
        </div>
      }/>

      {/* Units */}
      <Setting startEnhancer={glyphs.Kg({size: "25px"})} header={t("units.units", {defaultValue: "units"})} content={
        <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
          <NewSelect
            isDisabled
            options={unitsOptions}
            onChange={(e) => setUnit(Number(e?.id ?? 0))}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary
            }}
            valueOverrides={{
              defaultValue: unitsOptions.find(option => option.id === unit)
            }}
          />
        </div>
      }/>

      {/* Feed Day Start */}
      <Setting startEnhancer={glyphs.TimeToday({size: "25px"})} header={t("globalSettingsPages.globalSettings.feedDayStart", {defaultValue: "feed day start"})} content={
        <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
          <NewTimePicker
            valueRef={timePickerValueRef}
            overrides={{
              fieldStyle: NewFieldStyles.greyLightSecondary
            }}
            background
            valueOverrides={defaultFeedDayStart}
            onTimeSelect={(e) => setFeedDayStartChanged(true)}
          />
        </div>
      }/>

      {/* Milestones */}
      <Setting
        startEnhancer={glyphs.Setting({size: "25px"})}
        header={t("globalSettingsPages.globalSettings.milestones", {defaultValue: "milestones"})}
        content={ <>
          <div style={{display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: mobileXs ? "center" : "start"}}>
            <NewInput ref={milestoneNameRef}
              placeholder={t("milestones.general.newMilestone", {defaultValue: "New Milestone"})} 
              fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }} 
              isClearable
            />
            <NewButton
              content={t("buttons.add", {defaultValue: "add"})}
              overrides={{
                  baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
                  contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
                  hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
              }}
              onClick={handleAddMilestone}
            />
          </div>
          <div style={{ borderTop: "2px solid " + colors.greyLightPrimary, width: "100%", margin: "15px 0", display: "flex", justifyContent: "end" }}/>
          <div style={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: mobileXs ? "center" : "start"}}>
            {localMilestones.filter(m => !m.deleted).map(milestone => {
              const isDefault = milestone.milestone_id <= 4;

              return (isDefault?
                <p style={{width: "fit-content", height: "22px", backgroundColor: colors.greySecondary, borderRadius: "8px", padding: "0px 6px", display: "flex", alignItems: "center", ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>{milestone.name}</p>
                : <NewButton 
                content={milestone.name} 
                buttonType={ButtonTypes.tagButton}
                endEnhancer={glyphs.Close({size: "10px"})}
                onClick={() => handleDeleteMilestone(milestone.milestone_id)}
                key={`milestone:${milestone.milestone_id}`}
                overrides={{
                }}
              />)
            })}
          </div>
        </> }
      />
    </div>
    {/* Save/discard btns */}

    {dirtyData && createPortal(
      <div style={{ position: "absolute", zIndex: dirtyData ? "999999" : "0", bottom: "40px", left: "50%", transform: "translateX(-50%)", backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", }}>
        <NewButtonList
          contents={[t("globalSettingsPages.globalSettings.saveChanges", { defaultValue: "save changes" }), t("globalSettingsPages.globalSettings.discardChanges", { defaultValue: "discard changes" })]}
          onClick={(i => {
            if (i === 0) {
              if (true) {
                const timeZone = timeZoneRef.current?.value && timeZoneRef.current.value.id !== undefined && timeZoneRef.current.value.id !== null ? { time_zone: DateTime.now().setZone(String(timeZoneRef.current.value.id)).offset, time_zone_label: String(timeZoneRef.current.value.id) } : undefined;
                if (feedDayStartChanged || timeZoneChanged || (timePickerValueRef.current.hour !== undefined && timePickerValueRef.current.minute !== undefined)) {
                  // Feed day start is not given in local time --- AND should be restricted to within 24 hours.
                  const zoneOffset = !!timeZone?.time_zone ? timeZone.time_zone : farmSettings?.time_zone;
                  const rawFeedDayStart = timePickerValueRef.current.hour === undefined || timePickerValueRef.current.minute === undefined ? undefined : 60 * Number(timePickerValueRef.current.hour) + Number(timePickerValueRef.current.minute) - (zoneOffset ?? 0);
                  const feedDayStart = rawFeedDayStart !== undefined ? LocaleFarmDate.restrictTimeTo24Hours(rawFeedDayStart) : undefined;
                  const feedDayStartString = feedDayStart !== undefined ? `${Math.floor(feedDayStart / 60)}:${feedDayStart % 60}:00` : undefined;

                  updateFarmSettings(farm, timeZoneChanged ? timeZone : undefined, feedDayStartString);
                  setTimeZoneChanged(false);
                  setFeedDayStartChanged(false);
                  timePickerValueRef.current = { hour: undefined, minute: undefined };
                  timeZoneRef.current?.setValue({ label: farmSettings?.time_zone_label?.replace(/_/g, " ") ?? "", id: farmSettings?.time_zone })
                }
                if (maintainMilestones) {
                  const newMilestones = localMilestones
                    .filter(local => !milestones.some(db => db.milestone_id === local.milestone_id))
                    .map(m => ({ name: m.name }));
              
                  const updatedMilestones = localMilestones
                    .filter(local => milestones.some(db => db.milestone_id === local.milestone_id))
                    .map(m => ({ ...m }));
              
                  maintainMilestones({ data: [...updatedMilestones, ...newMilestones] });
                }
              }
            }
              
            else if (i === 1) {
              setLocalMilestones(milestones);
              if (milestoneNameRef.current?.value) {
                milestoneNameRef.current.setValue("")
              }
              
              setTimeZoneChanged(false);

              setFeedDayStartChanged(false);
              
              setDefaultFeedDayStart((oldDefaultValue) => {
                return { ...oldDefaultValue }
              })
              
              timePickerValueRef.current = { hour: undefined, minute: undefined };
              if (farmSettings?.time_zone_label) {
                const findSelectId = timeZoneOptions.find(({label}) => label === farmSettings.time_zone_label)?.id;
                timeZoneRef.current?.setValue({label: farmSettings.time_zone_label.replace(/_/g," ") , id: findSelectId !== undefined ? findSelectId : -1})
              } else {
                timeZoneRef.current?.setValue({ label: "", id: -1 })
              }
            }
          })}
          overrides={(i) => ({
            baseStyles: { backgroundColor: i === 0 ? colors.blueDarkPrimary : colors.greyDarkPrimary, border: i === 1 ? "3px solid "+colors.blueDarkPrimary : "none"},
            hoverStyles: { backgroundColor: i === 0 ? colors.blueDarkSecondary : colors.blueDarkPrimary },
          })}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal
          }}
        />
      </div>, document.body)
    }
  </> );
}